import { useEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';

//A pill that names how many options are checked, over a panel of checkbox rows. The search field
//appears only once the list is long enough to need it - an admin sees every branch, a scheduler
//usually sees two or three.
const SEARCH_THRESHOLD = 8;

const MultiSelectDropdown = ({
  options,
  selected,
  onChange,
  noun,
  nounPlural,
  emptyLabel,
  disabled = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  //what was checked when the panel opened, so the list settles once rather than resorting under the
  //cursor every time a box is ticked
  const [pinned, setPinned] = useState([]);
  const wrapperRef = useRef(null);

  //mousedown rather than click: unticking a box unmounts its check image before a document click
  //listener runs, so the target is already detached and the panel would read it as outside and close
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = value => {
    onChange(
      selected.includes(value) ? selected.filter(item => item !== value) : [...selected, value],
    );
  };

  const displayOptions = options
    .filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => (pinned.includes(b.value) ? 1 : 0) - (pinned.includes(a.value) ? 1 : 0));

  //select all acts on what the search has narrowed to, so the row never checks a box the reader
  //cannot see, and passes over options marked skipSelectAll, which can only be ticked one by one.
  //clear always drops the whole selection, filtered or not.
  const shownValues = displayOptions
    .filter(option => !option.skipSelectAll)
    .map(option => option.value);
  const allShownSelected =
    shownValues.length > 0 && shownValues.every(value => selected.includes(value));

  const summary =
    selected.length === 0
      ? emptyLabel
      : `${selected.length} ${selected.length === 1 ? noun : nounPlural}`;

  return (
    <div ref={wrapperRef} className='relative w-fit'>
      <button
        type='button'
        disabled={disabled}
        onClick={() => {
          if (!showDropdown) setPinned(selected);
          setShowDropdown(!showDropdown);
        }}
        className={`flex items-center gap-1 h-[40px] pl-3 pr-2 rounded border bg-white text-sm font-[450] disabled:bg-disabled-gray disabled:text-opaque-job-details-gray disabled:border-navy-light ${
          selected.length ? 'border-dark-blue text-dark-blue' : 'border-navy-light text-gray-blue'
        }`}
      >
        <span className='select-none'>{summary}</span>
        <img
          src={showDropdown ? '/show_less_arrow.svg' : '/show_more_arrow.svg'}
          alt=''
          className='h-[20px]'
        />
      </button>

      <div
        className={`absolute z-10 mt-1 w-[280px] flex flex-col p-[8px] bg-white border border-navy-light rounded-[8px] shadow-md text-sm ${
          showDropdown ? '' : 'hidden'
        }`}
      >
        {options.length > SEARCH_THRESHOLD && (
          <div className='flex items-center mb-1 bg-white rounded-lg border border-navy-light overflow-hidden'>
            <FaSearch className='ml-3 text-gray-blue w-[16px]' />
            <input
              type='text'
              placeholder='Search'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='p-[6px] flex-1 text-dark-blue border-none focus:ring-0'
            />
          </div>
        )}

        <div className='flex justify-between px-[8px] pb-[6px] border-b border-navy-light'>
          <button
            type='button'
            onClick={() => onChange([...new Set([...selected, ...shownValues])])}
            disabled={allShownSelected || !shownValues.length}
            className='text-dark-blue hover:underline disabled:text-opaque-job-details-gray disabled:no-underline'
          >
            {searchTerm ? `Select ${shownValues.length} shown` : 'Select all'}
          </button>
          <button
            type='button'
            onClick={() => onChange([])}
            disabled={!selected.length}
            className='text-dark-blue hover:underline disabled:text-opaque-job-details-gray disabled:no-underline'
          >
            Clear
          </button>
        </div>

        <div className='max-h-[300px] overflow-y-auto pt-[2px]'>
          {displayOptions.map(option => (
            <button
              key={option.value}
              type='button'
              onClick={() => toggleOption(option.value)}
              className='flex w-full items-center p-[8px] text-left hover:bg-gray-light rounded'
            >
              <div
                className={`shrink-0 w-[16px] h-[16px] rounded-[4px] select-none ${
                  selected.includes(option.value) ? 'bg-dark-blue' : 'bg-white border'
                }`}
              >
                {selected.includes(option.value) && (
                  <img src='/check.svg' alt='' className='w-full' />
                )}
              </div>
              <span className='pl-[12px] select-none text-dark-blue'>{option.label}</span>
            </button>
          ))}

          {!displayOptions.length && (
            <p className='p-[8px] text-opaque-job-details-gray'>No matches</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
